import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  TextField,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { ExpandMore, History } from "@mui/icons-material";
import { AuthContext } from "../../context/AuthContext";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { workflowAPI } from "../../services/api";
import { sanitizeHtml } from "../../utils/htmlUtils";
import { stripHtml } from "../../utils/htmlUtils";

// Draggable Card Component
const DraggableCard = ({ card, onClick, onStatusChange }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `card-${card.id}`,
      data: { type: "card", card },
    });

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleStatusChange = async (event) => {
    event.stopPropagation();
    const newStatus = event.target.value;
    if (onStatusChange && newStatus !== card.card_status) {
      await onStatusChange(card.id, newStatus);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "success";
      case "Rejected":
        return "error";
      case "On Hold":
        return "warning";
      case "Approved":
        return "info";
      default:
        return "default";
    }
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 1.5,
        cursor: "grab",
        "&:hover": {
          boxShadow: 3,
        },
        "&:active": {
          cursor: "grabbing",
        },
      }}
      onClick={() => onClick(card)}
      {...listeners}
      {...attributes}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={1}
        >
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            noWrap
            sx={{ flex: 1, mr: 1 }}
          >
            {card.title}
          </Typography>
          <FormControl
            size="small"
            sx={{ minWidth: 90 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Select
              value={card.card_status || "Active"}
              onChange={handleStatusChange}
              onClick={(e) => e.stopPropagation()}
              sx={{
                height: 28,
                fontSize: "0.75rem",
                "& .MuiSelect-select": {
                  py: 0.5,
                  px: 1,
                },
              }}
            >
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
              <MenuItem value="On Hold">On Hold</MenuItem>
              <MenuItem value="Approved">Approved</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box display="flex" flexDirection="column" gap={0.5} mt={1}>
          <Typography variant="caption" color="text.secondary">
            Owner: {card.owner_username}
          </Typography>
          {card.assigned_username && (
            <Typography variant="caption" color="text.secondary">
              Assigned: {card.assigned_username}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            Time: {card.time_in_state}
          </Typography>
          <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
            {card.estimated_hours && (
              <Chip
                label={`${card.estimated_hours}h`}
                size="small"
                variant="outlined"
              />
            )}
            {card.estimated_days && (
              <Chip
                label={`${card.estimated_days}d`}
                size="small"
                variant="outlined"
              />
            )}
            {card.estimated_story_points && (
              <Chip
                label={`${card.estimated_story_points} SP`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Droppable Column Component
const DroppableColumn = ({ column, cards, onCardClick, onStatusChange }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `column-${column.id}`,
    data: { type: "column", column },
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        minWidth: 280,
        maxWidth: 280,
        mr: 2,
        flexShrink: 0,
        opacity: isOver ? 0.8 : 1,
      }}
    >
      <Paper
        sx={{
          p: 2,
          backgroundColor: "#f5f5f5",
          minHeight: "400px",
          maxHeight: "calc(100vh - 300px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            fontWeight: 600,
            mb: 2,
            pb: 1,
            borderBottom: "2px solid",
            borderColor: "primary.main",
          }}
        >
          {column.title}
          <Chip
            label={cards.length}
            size="small"
            sx={{ ml: 1 }}
            color="primary"
            variant="outlined"
          />
        </Typography>
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {cards.map((card) => (
            <DraggableCard
              key={card.id}
              card={card}
              onClick={onCardClick}
              onStatusChange={onStatusChange}
            />
          ))}
          {cards.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", mt: 4 }}
            >
              No cards
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

// Card Detail Modal
const CardDetailModal = ({
  open,
  onClose,
  card,
  onUpdate,
  users,
  workflowColumns,
}) => {
  const { user: currentUser } = useContext(AuthContext);
  const [assignedTo, setAssignedTo] = useState(card?.assigned_to || null);
  const [workflowStatus, setWorkflowStatus] = useState(
    card?.workflow_status || "",
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tabValue, setTabValue] = useState(0);

  // Editable fields for Engineering Lead/Engineer
  const [technicalImpact, setTechnicalImpact] = useState(
    card?.technical_impact || "",
  );
  const [testingImpact, setTestingImpact] = useState(
    card?.testing_impact || "",
  );
  const [componentStructure, setComponentStructure] = useState(
    card?.component_structure
      ? JSON.stringify(card.component_structure, null, 2)
      : "",
  );
  const [estimatedHours, setEstimatedHours] = useState(
    card?.estimated_hours || "",
  );
  const [estimatedDays, setEstimatedDays] = useState(
    card?.estimated_days || "",
  );
  const [estimatedStoryPoints, setEstimatedStoryPoints] = useState(
    card?.estimated_story_points || "",
  );
  const [changeComment, setChangeComment] = useState("");
  const [auditTrail, setAuditTrail] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);

  const canEditDetails =
    currentUser?.workflow_role === "Engineering Lead" ||
    currentUser?.workflow_role === "Engineer";
  const auditTrailTabIndex = canEditDetails ? 2 : 1;

  // Check if there are any changes to the editable fields
  const hasChanges = () => {
    if (!card) return false;

    const currentComponentStructure = card.component_structure
      ? typeof card.component_structure === "string"
        ? card.component_structure
        : JSON.stringify(card.component_structure, null, 2)
      : "";

    return (
      (technicalImpact || "") !== (card.technical_impact || "") ||
      (testingImpact || "") !== (card.testing_impact || "") ||
      (componentStructure || "") !== currentComponentStructure ||
      (estimatedHours || "") !== (card.estimated_hours?.toString() || "") ||
      (estimatedDays || "") !== (card.estimated_days?.toString() || "") ||
      (estimatedStoryPoints || "") !==
        (card.estimated_story_points?.toString() || "")
    );
  };

  useEffect(() => {
    if (card) {
      setAssignedTo(card.assigned_to || null);
      setWorkflowStatus(card.workflow_status || "");
      setTechnicalImpact(card.technical_impact || "");
      setTestingImpact(card.testing_impact || "");
      setComponentStructure(
        card.component_structure
          ? typeof card.component_structure === "string"
            ? card.component_structure
            : JSON.stringify(card.component_structure, null, 2)
          : "",
      );
      setEstimatedHours(card.estimated_hours || "");
      setEstimatedDays(card.estimated_days || "");
      setEstimatedStoryPoints(card.estimated_story_points || "");
      setChangeComment("");
      if (open && card.id) {
        loadAuditTrail();
      }
    }
  }, [card, open]);

  const loadAuditTrail = async () => {
    if (!card?.id) return;
    try {
      setLoadingAudit(true);
      const response = await workflowAPI.getAudit(card.id);
      setAuditTrail(response.data || []);
    } catch (err) {
      console.error("Failed to load audit trail:", err);
    } finally {
      setLoadingAudit(false);
    }
  };

  const handleSaveStatus = async () => {
    if (!card) return;

    setLoading(true);
    setError("");

    try {
      await workflowAPI.updateStatus(card.id, {
        workflow_status: workflowStatus,
        assigned_to: assignedTo,
      });
      await loadAuditTrail(); // Reload audit trail after status update
      onUpdate();
      onClose();
    } catch (err) {
      console.error("Failed to update card:", err);
      setError(err.response?.data?.error || "Failed to update card");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDetails = async () => {
    if (!card || !canEditDetails) return;

    if (!changeComment.trim()) {
      setError("Please provide a comment explaining the reason for the change");
      return;
    }

    setLoading(true);
    setError("");

    try {
      let parsedComponentStructure = null;
      if (componentStructure.trim()) {
        try {
          parsedComponentStructure = JSON.parse(componentStructure);
        } catch (e) {
          setError("Component structure must be valid JSON");
          setLoading(false);
          return;
        }
      }

      await workflowAPI.updateDetails(card.id, {
        technical_impact: technicalImpact,
        testing_impact: testingImpact,
        component_structure: parsedComponentStructure,
        estimated_hours: estimatedHours ? parseFloat(estimatedHours) : null,
        estimated_days: estimatedDays ? parseFloat(estimatedDays) : null,
        estimated_story_points: estimatedStoryPoints
          ? parseInt(estimatedStoryPoints)
          : null,
        comment: changeComment,
      });

      setChangeComment("");
      await loadAuditTrail();
      onUpdate();
      setError("");
    } catch (err) {
      console.error("Failed to update details:", err);
      setError(err.response?.data?.error || "Failed to update details");
    } finally {
      setLoading(false);
    }
  };

  if (!card) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{card.title}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{ mb: 2 }}
        >
          <Tab label="Overview" />
          {canEditDetails && <Tab label="Edit Details" />}
          <Tab label="Audit Trail" icon={<History />} iconPosition="start" />
        </Tabs>

        {tabValue === 0 && (
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Owner
              </Typography>
              <Typography variant="body1">
                {card.owner_username} ({card.owner_email})
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Workflow Status</InputLabel>
              <Select
                value={workflowStatus}
                onChange={(e) => setWorkflowStatus(e.target.value)}
                label="Workflow Status"
              >
                {workflowColumns && workflowColumns.length > 0 ? (
                  workflowColumns.map((col) => (
                    <MenuItem key={col.column_name} value={col.column_name}>
                      {col.display_name || col.column_name}
                    </MenuItem>
                  ))
                ) : (
                  <>
                    <MenuItem value="Owner">Owner</MenuItem>
                    <MenuItem value="Engineering Lead">
                      Engineering Lead
                    </MenuItem>
                    <MenuItem value="Engineer">Engineer</MenuItem>
                    <MenuItem value="Approved">Approved</MenuItem>
                  </>
                )}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Assign To</InputLabel>
              <Select
                value={assignedTo || ""}
                onChange={(e) => setAssignedTo(e.target.value || null)}
                label="Assign To"
              >
                <MenuItem value="">Unassigned</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.username} ({user.workflow_role || "No role"})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Time in Current State
              </Typography>
              <Typography variant="body1">{card.time_in_state}</Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Estimates
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {card.estimated_hours && (
                  <Chip label={`${card.estimated_hours} hours`} size="small" />
                )}
                {card.estimated_days && (
                  <Chip label={`${card.estimated_days} days`} size="small" />
                )}
                {card.estimated_story_points && (
                  <Chip
                    label={`${card.estimated_story_points} story points`}
                    size="small"
                  />
                )}
              </Box>
            </Box>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle2">Description</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    maxHeight: "300px",
                    overflowY: "auto",
                    p: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(card.description || "No description"),
                  }}
                />
              </AccordionDetails>
            </Accordion>

            {card.technical_impact && (
              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">Technical Impact</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" whiteSpace="pre-wrap">
                    {card.technical_impact}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            )}

            {card.testing_impact && (
              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">Testing Impact</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" whiteSpace="pre-wrap">
                    {card.testing_impact}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        )}

        {tabValue === 1 && canEditDetails && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              As {currentUser?.workflow_role}, you can update the technical
              analysis, testing analysis, component structure, and estimates.
              All changes will be audited.
            </Alert>

            <TextField
              fullWidth
              multiline
              rows={6}
              label="Technical Impact Analysis"
              value={technicalImpact}
              onChange={(e) => setTechnicalImpact(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              multiline
              rows={6}
              label="Testing Impact Analysis"
              value={testingImpact}
              onChange={(e) => setTestingImpact(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              multiline
              rows={8}
              label="Technical Design - Component Impact Analysis (JSON)"
              value={componentStructure}
              onChange={(e) => setComponentStructure(e.target.value)}
              sx={{ mb: 2 }}
              helperText="Enter valid JSON format for component structure"
            />

            <Box display="flex" gap={2} sx={{ mb: 2 }}>
              <TextField
                label="Estimated Hours"
                type="number"
                value={estimatedHours}
                onChange={(e) => setEstimatedHours(e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Estimated Days"
                type="number"
                value={estimatedDays}
                onChange={(e) => setEstimatedDays(e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Estimated Story Points"
                type="number"
                value={estimatedStoryPoints}
                onChange={(e) => setEstimatedStoryPoints(e.target.value)}
                sx={{ flex: 1 }}
              />
            </Box>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Comment (Required)"
              value={changeComment}
              onChange={(e) => setChangeComment(e.target.value)}
              placeholder="Please provide a reason for making these changes..."
              required
              error={hasChanges() && !changeComment.trim()}
              helperText={
                hasChanges() && !changeComment.trim()
                  ? "A comment is required to save changes"
                  : "Please provide a reason for making these changes"
              }
              sx={{ mb: 2 }}
            />
          </Box>
        )}

        {tabValue === auditTrailTabIndex && (
          <Box>
            {loadingAudit ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : auditTrail.length === 0 ? (
              <Alert severity="info">No audit trail records found.</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date/Time</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Field</TableCell>
                      <TableCell>Old Value</TableCell>
                      <TableCell>New Value</TableCell>
                      <TableCell>Comment</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditTrail.map((audit) => (
                      <TableRow key={audit.id}>
                        <TableCell>
                          {new Date(audit.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {audit.username} ({audit.email})
                        </TableCell>
                        <TableCell>{audit.field_name}</TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {audit.old_value || "(empty)"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {audit.new_value || "(empty)"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {audit.comment || "-"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {tabValue === 0 && (
          <Button
            onClick={handleSaveStatus}
            variant="contained"
            disabled={loading}
          >
            {loading ? "Saving..." : "Save Status"}
          </Button>
        )}
        {tabValue === 1 && canEditDetails && (
          <Button
            onClick={handleSaveDetails}
            variant="contained"
            disabled={loading || !hasChanges() || !changeComment.trim()}
            title={
              !hasChanges()
                ? "No changes detected"
                : !changeComment.trim()
                  ? "Comment is required"
                  : "Save changes"
            }
          >
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const WorkflowBoard = ({ activeConfigurationId }) => {
  const [columns, setColumns] = useState([]);
  const [workflowColumns, setWorkflowColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedCard, setSelectedCard] = useState(null);
  const [users, setUsers] = useState([]);
  const [activeId, setActiveId] = useState(null);

  // Configure sensors for drag and drop (including touch for mobile/iPad)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
  );

  useEffect(() => {
    if (activeConfigurationId) {
      loadBoard();
      loadUsers();
      loadWorkflowConfig();
    }
  }, [activeConfigurationId]);

  const loadBoard = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await workflowAPI.getBoard(activeConfigurationId);
      setColumns(response.data.columns || []);
    } catch (err) {
      console.error("Failed to load workflow board:", err);
      setError(err.response?.data?.error || "Failed to load workflow board");
    } finally {
      setLoading(false);
    }
  };

  const loadWorkflowConfig = async () => {
    try {
      const response = await workflowAPI.getConfig(activeConfigurationId);
      setWorkflowColumns(response.data || []);
    } catch (err) {
      console.error("Failed to load workflow config:", err);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await workflowAPI.getUsersByRole();
      setUsers(response.data || []);
    } catch (err) {
      console.error("Failed to load users:", err);
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeId = active.id.toString();
    const overId = over.id.toString();

    // Check if we're dragging a card
    if (!activeId.startsWith("card-")) return;

    // Check if we're dropping on a column
    if (!overId.startsWith("column-")) return;

    const cardId = activeId.replace("card-", "");
    const columnId = overId.replace("column-", "");

    // Find the card
    let card = null;
    for (const col of columns) {
      const found = col.cards.find((c) => c.id.toString() === cardId);
      if (found) {
        card = found;
        break;
      }
    }

    if (!card || card.workflow_status === columnId) return;

    // Update the card status
    try {
      await workflowAPI.updateStatus(card.id, {
        workflow_status: columnId,
      });
      // Reload the board
      await loadBoard();
    } catch (err) {
      console.error("Failed to update card status:", err);
      setError(err.response?.data?.error || "Failed to update card status");
    }
  };

  const handleCardClick = (card) => {
    setSelectedCard(card);
  };

  const handleModalClose = () => {
    setSelectedCard(null);
  };

  const handleModalUpdate = () => {
    loadBoard();
  };

  const handleStatusChange = async (cardId, newStatus) => {
    try {
      // Find the current card to get workflow_status
      let currentCard = null;
      for (const col of columns) {
        const card = col.cards.find((c) => c.id === cardId);
        if (card) {
          currentCard = card;
          break;
        }
      }

      if (!currentCard) return;

      // Update status via API
      await workflowAPI.updateStatus(cardId, {
        workflow_status: currentCard.workflow_status,
        assigned_to: currentCard.assigned_to,
        card_status: newStatus,
      });

      // Reload board to reflect changes
      loadBoard();
    } catch (err) {
      console.error("Failed to update card status:", err);
      setError(err.response?.data?.error || "Failed to update card status");
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && !columns.length) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Box
          sx={{
            display: "flex",
            overflowX: "auto",
            pb: 2,
            minHeight: "500px",
          }}
        >
          {columns.map((column) => (
            <DroppableColumn
              key={column.id}
              column={column}
              cards={column.cards}
              onCardClick={handleCardClick}
              onStatusChange={handleStatusChange}
            />
          ))}
        </Box>

        <DragOverlay>
          {activeId ? (
            <Card sx={{ width: 280, opacity: 0.8 }}>
              <CardContent>
                <Typography variant="subtitle2">Dragging...</Typography>
              </CardContent>
            </Card>
          ) : null}
        </DragOverlay>
      </DndContext>

      <CardDetailModal
        open={!!selectedCard}
        onClose={handleModalClose}
        card={selectedCard}
        onUpdate={handleModalUpdate}
        users={users}
        workflowColumns={workflowColumns}
      />
    </Box>
  );
};

export default WorkflowBoard;
